/**
 * Mock Currency Data
 * Contains exchange rates and currency information for multi-currency support
 */

import type { CurrencyCode } from '../utils/formatting';

/**
 * Currency Information Interface
 */
export interface CurrencyInfo {
  code: CurrencyCode;
  name: string;
  symbol: string;
  country: string;
  flag: string;
  decimalPlaces: number;
  isBaseCurrency: boolean;
  isActive: boolean;
}

/**
 * Exchange Rate Interface
 */
export interface ExchangeRate {
  fromCurrency: CurrencyCode;
  toCurrency: CurrencyCode;
  rate: number;
  lastUpdated: Date;
  source: string;
}

/**
 * Supported Currencies
 */
export const mockCurrencies: CurrencyInfo[] = [
  {
    code: 'MYR',
    name: 'Malaysian Ringgit',
    symbol: 'RM',
    country: 'Malaysia',
    flag: '🇲🇾',
    decimalPlaces: 2,
    isBaseCurrency: true,
    isActive: true,
  },
  {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    country: 'United States',
    flag: '🇺🇸',
    decimalPlaces: 2,
    isBaseCurrency: false,
    isActive: true,
  },
  {
    code: 'EUR',
    name: 'Euro',
    symbol: '€',
    country: 'European Union',
    flag: '🇪🇺',
    decimalPlaces: 2,
    isBaseCurrency: false,
    isActive: true,
  },
  {
    code: 'SGD',
    name: 'Singapore Dollar',
    symbol: 'S$',
    country: 'Singapore',
    flag: '🇸🇬',
    decimalPlaces: 2,
    isBaseCurrency: false,
    isActive: true,
  },
  {
    code: 'AED',
    name: 'UAE Dirham',
    symbol: 'د.إ',
    country: 'United Arab Emirates',
    flag: '🇦🇪',
    decimalPlaces: 2,
    isBaseCurrency: false,
    isActive: true,
  },
  {
    code: 'GBP',
    name: 'British Pound',
    symbol: '£',
    country: 'United Kingdom',
    flag: '🇬🇧',
    decimalPlaces: 2,
    isBaseCurrency: false,
    isActive: true,
  },
  {
    code: 'SAR',
    name: 'Saudi Riyal',
    symbol: 'ر.س',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    decimalPlaces: 2,
    isBaseCurrency: false,
    isActive: true,
  },
  {
    code: 'IDR',
    name: 'Indonesian Rupiah',
    symbol: 'Rp',
    country: 'Indonesia',
    flag: '🇮🇩',
    decimalPlaces: 0,
    isBaseCurrency: false,
    isActive: true,
  },
];

/**
 * Exchange Rates (Base Currency: MYR)
 * All rates are FROM MYR to other currencies
 * Updated: 2025-12-30
 */
export const mockExchangeRates: ExchangeRate[] = [
  // MYR to USD
  {
    fromCurrency: 'MYR',
    toCurrency: 'USD',
    rate: 0.22,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },
  // USD to MYR
  {
    fromCurrency: 'USD',
    toCurrency: 'MYR',
    rate: 4.55,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },

  // MYR to EUR
  {
    fromCurrency: 'MYR',
    toCurrency: 'EUR',
    rate: 0.21,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },
  // EUR to MYR
  {
    fromCurrency: 'EUR',
    toCurrency: 'MYR',
    rate: 4.76,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },

  // MYR to SGD
  {
    fromCurrency: 'MYR',
    toCurrency: 'SGD',
    rate: 0.3,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },
  // SGD to MYR
  {
    fromCurrency: 'SGD',
    toCurrency: 'MYR',
    rate: 3.33,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },

  // MYR to AED
  {
    fromCurrency: 'MYR',
    toCurrency: 'AED',
    rate: 0.81,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },
  // AED to MYR
  {
    fromCurrency: 'AED',
    toCurrency: 'MYR',
    rate: 1.24,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },

  // MYR to GBP
  {
    fromCurrency: 'MYR',
    toCurrency: 'GBP',
    rate: 0.18,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },
  // GBP to MYR
  {
    fromCurrency: 'GBP',
    toCurrency: 'MYR',
    rate: 5.56,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },

  // MYR to SAR
  {
    fromCurrency: 'MYR',
    toCurrency: 'SAR',
    rate: 0.83,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },
  // SAR to MYR
  {
    fromCurrency: 'SAR',
    toCurrency: 'MYR',
    rate: 1.21,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },

  // MYR to IDR
  {
    fromCurrency: 'MYR',
    toCurrency: 'IDR',
    rate: 3500.0,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },
  // IDR to MYR
  {
    fromCurrency: 'IDR',
    toCurrency: 'MYR',
    rate: 0.000286,
    lastUpdated: new Date('2025-12-30T08:00:00'),
    source: 'Bank Negara Malaysia',
  },
];

/**
 * Helper to get currency info by code
 */
export function getCurrencyInfo(code: CurrencyCode): CurrencyInfo | undefined {
  return mockCurrencies.find((currency) => currency.code === code);
}

/**
 * Helper to get all active currencies
 */
export function getActiveCurrencies(): CurrencyInfo[] {
  return mockCurrencies.filter((currency) => currency.isActive);
}

/**
 * Helper to get base currency (MYR)
 */
export function getBaseCurrency(): CurrencyInfo {
  return mockCurrencies.find((currency) => currency.isBaseCurrency)!;
}

/**
 * Helper to get exchange rate
 */
export function getExchangeRate(
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): ExchangeRate | undefined {
  return mockExchangeRates.find(
    (rate) => rate.fromCurrency === fromCurrency && rate.toCurrency === toCurrency
  );
}

/**
 * Helper to convert amount between currencies
 */
export function convertCurrency(
  amount: number,
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): { convertedAmount: number; rate: number } | null {
  // Same currency, no conversion needed
  if (fromCurrency === toCurrency) {
    return { convertedAmount: amount, rate: 1.0 };
  }

  const rate = getExchangeRate(fromCurrency, toCurrency);
  if (!rate) {
    return null;
  }

  const convertedAmount = amount * rate.rate;
  return { convertedAmount, rate: rate.rate };
}

/**
 * Helper to get all exchange rates for a currency
 */
export function getExchangeRatesForCurrency(
  currency: CurrencyCode
): ExchangeRate[] {
  return mockExchangeRates.filter((rate) => rate.fromCurrency === currency);
}

/**
 * Helper to format currency with proper symbol and decimal places
 */
export function formatCurrencyWithInfo(
  amount: number,
  currencyCode: CurrencyCode
): string {
  const currencyInfo = getCurrencyInfo(currencyCode);
  if (!currencyInfo) {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }

  const formattedAmount = amount.toFixed(currencyInfo.decimalPlaces);
  return `${currencyInfo.symbol}${formattedAmount}`;
}

/**
 * Currency conversion table (for quick reference)
 * 1 MYR equals:
 */
export const CURRENCY_CONVERSION_TABLE = {
  MYR: 1.0,
  USD: 0.22,
  EUR: 0.21,
  SGD: 0.3,
  AED: 0.81,
  GBP: 0.18,
  SAR: 0.83,
  IDR: 3500.0,
};

/**
 * Reverse conversion table (to MYR)
 * 1 unit equals x MYR:
 */
export const TO_MYR_CONVERSION_TABLE = {
  MYR: 1.0,
  USD: 4.55,
  EUR: 4.76,
  SGD: 3.33,
  AED: 1.24,
  GBP: 5.56,
  SAR: 1.21,
  IDR: 0.000286,
};
